/**
 * Where a chat session id lives between page loads, and the legacy data that
 * used to live beside it.
 *
 * Extracted from `useAgentChat.ts` when that file crossed the repo's 1000-line
 * ceiling. Pure and React-free on purpose: nothing here reads component state,
 * so it is directly testable and cannot be the reason a turn is lost.
 *
 * NO message content is stored. Only the session id, so a reload can rejoin the
 * conversation; the transcript comes from the server.
 */

/**
 * localStorage key for session ID only, NO message content stored.
 *
 * Scoped to the signed-in user AND the workspace. It was the workspace alone
 * until 2026-10-19: a second account signed in to the same browser and
 * workspace then resumed the first account's session id. The server keeps
 * sessions per user, so nothing crossed accounts, but two people's turns
 * shared one session id in traces (Langfuse session 1792363801359-fggrh97).
 */
export function sessionKey(userId: string, workspaceId: string): string {
  return `agent_session_${userId}_${workspaceId}`;
}

/** The workspace-only key every session id was stored under before 2026-10-19. */
function workspaceOnlySessionKey(workspaceId: string): string {
  return `agent_session_${workspaceId}`;
}

/** Legacy key used for message storage — only referenced for cleanup */
function legacyMessagesKey(workspaceId: string): string {
  return `agent_messages_${workspaceId}`;
}

/** The stored session id for this user in this workspace; null without both. */
export function loadStoredSession(
  userId: string | null,
  workspaceId: string | null,
): string | null {
  if (!userId || !workspaceId) return null;
  return localStorage.getItem(sessionKey(userId, workspaceId));
}

/** Remember the session id for this user in this workspace; a no-op without both. */
export function storeSession(
  userId: string | null,
  workspaceId: string | null,
  sessionId: string,
): void {
  if (!userId || !workspaceId) return;
  localStorage.setItem(sessionKey(userId, workspaceId), sessionId);
}

/**
 * One-time cleanup of legacy data: the message buckets, and the workspace-only
 * session key. That key is not migrated: in a browser two accounts shared, it
 * may hold the OTHER account's session id, which is the case it is retired for.
 */
export function cleanupLegacyStorage(workspaceId: string): void {
  for (const key of [legacyMessagesKey(workspaceId), workspaceOnlySessionKey(workspaceId)]) {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
    }
  }
}
